import React, { useEffect, useCallback, useMemo, useState } from 'react'
import clsx from 'clsx'
import { useMutation } from '@apollo/client'
import { useRouteMatch, useParams } from 'react-router-dom'
import PageHeader from 'components/page-header/page-header'
import { formatNameForDisplay, getInitials } from 'lib/text-display'
import DisplayContact from './details-components/display-contact'
import LeadDetails from './details-components/lead-details'
import { useIsTablet } from 'lib/hooks'
import {
  Container,
  Box,
  Hidden,
  Avatar,
  Button,
  Typography,
} from '@material-ui/core'
import OpenInNewOutlinedIcon from 'components/material-symbol/icons/OpenInNew'
import { useGetLead } from '../api-hooks'
import DeleteLead from './details-components/delete-lead'
import LeadActionButtons from './header-components/action-buttons'
import { coachingLeadViewedMutation } from '../mutations'
import ApolloDataView from 'components/apollo-data-view'
import LoadingView from '../loading-view'
import NotFound from 'components/not-found.js'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'
import Alert from '@material-ui/lab/Alert'
import { useStyles } from './styles'
import { formatLeadHeaderMeta } from './utils'

const TIPS_LEARN_MORE_URL =
  'https://vt.lightspeedvt.com/share/redirect.cfm?signInURL=https://pgacoach.lightspeedvt.com&dest=https://vt.lightspeedvt.com/trainingCenter/course/117834'

const RECOMMENDATION_TIPS = [
  'When you respond to your leads ask questions to gain a deeper understanding of their needs.',
  'Make contact with your leads no later than 24 hours if possible to ensure the lead is in the same mindset.',
  'Not available or not a good fit? Let this lead know, and if possible offer a referral to a fellow member who might be able to help.',
  'Did you know one (1) lead can refer five (5) additional students by referring friends and family? Focus on building relationships.',
]

const LeadShowPage = ({ coachingInquiry, leadId }) => {
  const isTablet = useIsTablet()
  const classes = useStyles()
  const { path } = useRouteMatch()
  const [tipContent] = useState(
    () =>
      RECOMMENDATION_TIPS[
        Math.floor(Math.random() * RECOMMENDATION_TIPS.length)
      ],
  )

  const leadContact = coachingInquiry?.contact
  const leadName = formatNameForDisplay(
    `${leadContact?.firstName} ${leadContact?.lastName}`,
  )
  const leadTime = coachingInquiry?.createdAt
  const leadIntent = coachingInquiry?.intent
  const contactMethod = coachingInquiry?.contactMethod

  const headerMeta = useMemo(
    () =>
      formatLeadHeaderMeta({
        createdAt: leadTime,
        intent: leadIntent,
      }),
    [leadTime, leadIntent],
  )

  const crumbs = [
    {
      label: 'Leads',
      to: `${path
        .split('/')
        .slice(0, 2)
        .join('/')}/leads`,
    },
    {
      label: leadName,
      to: `${path}/${leadId}`,
    },
  ]

  return (
    <Box
      className={clsx(classes.root, isTablet && classes.rootWithStickyActions)}
    >
      <PageHeader
        data-testid="lead-header"
        title={leadName}
        crumbs={crumbs}
        hideCrumbs
        avatar={
          <Hidden xsDown>
            <Avatar className={classes.studentHeaderAvatar}>
              {getInitials(leadName)}
            </Avatar>
          </Hidden>
        }
        subtitle={headerMeta}
        backTo={crumbs[0].to}
        actions={
          <Hidden smDown>
            <LeadActionButtons contact={leadContact} leadId={leadId} />
          </Hidden>
        }
      />
      <Container>
        {leadContact?.banned ? (
          <Alert severity="warning" className={classes.spamAlert}>
            This lead has been flagged as <i>potential</i> spam.
          </Alert>
        ) : null}
        <Box className={classes.infoCardsGrid}>
          <DisplayContact contact={leadContact} />
          <LeadDetails
            timeCreatedAt={leadTime}
            intent={leadIntent}
            contactMethod={contactMethod}
          />
        </Box>
        <SensitiveInfoWrapper>
          <DeleteLead lead={coachingInquiry} />
        </SensitiveInfoWrapper>
        <Box className={classes.tipsSection}>
          <Box className={classes.coachPlusSection}>
            <Box className={classes.coachPlusCopy}>
              <Typography className={classes.planLabel}>
                Tips from PGA
              </Typography>
              <Typography className={classes.coachPlusCaption}>
                {tipContent}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              href={TIPS_LEARN_MORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.coachPlusButton}
              startIcon={<OpenInNewOutlinedIcon />}
            >
              Learn More
            </Button>
          </Box>
        </Box>
      </Container>
      {isTablet ? (
        <Box className={classes.stickyActionBar}>
          <Box className={classes.stickyActionBarInner}>
            <LeadActionButtons
              contact={leadContact}
              leadId={leadId}
              useStickyIcons
            />
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}

const LeadIndexPage = () => {
  const { leadId } = useParams()

  const { loading, data, error } = useGetLead(leadId)

  const [coachingLeadViewed] = useMutation(coachingLeadViewedMutation)
  const leadViewedMutation = useCallback(async () => {
    try {
      await coachingLeadViewed({
        variables: { id: leadId },
      })
    } catch (e) {
      console.error(e.message)
    }
  }, [leadId, coachingLeadViewed])

  useEffect(() => {
    const alreadyViewed = data?.coachingInquiry?.viewedAt != null
    if (!alreadyViewed && data?.coachingInquiry != null) {
      leadViewedMutation()
    }
  }, [data?.coachingInquiry, leadViewedMutation])

  return (
    <ApolloDataView
      error={error}
      loading={loading}
      data={data?.coachingInquiry}
    >
      {{
        errorView: (
          <NotFound
            subtext={'Lead was not found.'}
            returnUrl={'/pga-coach/leads'}
          />
        ),
        emptyView: (
          <NotFound
            subtext={'Lead was not found.'}
            returnUrl={'/pga-coach/leads'}
          />
        ),
        loadingView: <LoadingView />,
        dataView: coachingInquiry => (
          <LeadShowPage coachingInquiry={coachingInquiry} leadId={leadId} />
        ),
      }}
    </ApolloDataView>
  )
}

export default LeadIndexPage
